import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/dialogs";
import RichText from "@/components/RichText";
import { Pencil, Plus, GripVertical, Archive, RotateCcw, Download, Upload, ChevronUp, ChevronDown } from "lucide-react";

const FREQ = ["monthly", "quarterly", "annual", "one_off"];
const PER_YEAR = { monthly: 12, quarterly: 4, annual: 1 };
const FREQ_LABEL = { monthly: "monthly", quarterly: "quarterly", annual: "annual", one_off: "one-off" };
const BLANK = { client_name: "", internal_name: "", category: "", short_description: "", full_scope: "", base_fee: 0, minimum_fee: 0, billing_frequency: "monthly", vat_treatment: "standard", included: "", cass_responsibilities: "", client_responsibilities: "", create_loe_schedule: true };

export default function Services() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "pricing_admin"].includes(user?.role);
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [nw, setNw] = useState(BLANK);
  const [secOrder, setSecOrder] = useState([]);
  const [secDrag, setSecDrag] = useState(null);
  const [svcDrag, setSvcDrag] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [rbList, setRbList] = useState([]);

  const downloadComponents = async () => {
    try {
      const res = await api.get("/services/components.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "cass-pricing-components.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the components spreadsheet"); }
  };
  const importComponents = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/services/components/import?dry_run=true", fd);
      setPreviewFile(file);
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the spreadsheet"); }
  };
  const applyImport = async () => {
    if (!previewFile) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", previewFile);
    try {
      const { data } = await api.post("/services/components/import", fd);
      toast.success(`Imported: ${data.services_updated} service${data.services_updated === 1 ? "" : "s"} updated, ${data.components_total} components`);
      if (data.warnings?.length) toast.warning(data.warnings.slice(0, 3).join(" · "));
      setPreview(null); setPreviewFile(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const changeEditFreq = (v) => {
    const from = edit.pricing.billing_frequency || "monthly";
    if (from === v) return;
    const p = { ...edit.pricing, billing_frequency: v };
    if (PER_YEAR[from] && PER_YEAR[v]) {
      const f = PER_YEAR[from] / PER_YEAR[v];
      p.base_fee = Math.round(Number(edit.pricing.base_fee || 0) * f * 100) / 100;
      p.minimum_fee = Math.round(Number(edit.pricing.minimum_fee || 0) * f * 100) / 100;
      toast.info(`Fees converted to ${FREQ_LABEL[v]}: base ${gbp(p.base_fee)} (same annual value)`);
    } else {
      toast.info(`Billing set to ${FREQ_LABEL[v]} — fees left unchanged`);
    }
    setEdit({ ...edit, pricing: p });
  };
  const load = () => Promise.all([
    api.get("/services?include_archived=true").then((r) => setServices(r.data)),
    api.get("/service-sections").then((r) => setSecOrder(r.data.order || [])).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);
  useEffect(() => { if (canEdit) api.get("/requirement-blocks").then((r) => setRbList(r.data)).catch(() => {}); }, [canEdit]);

  const byCat = services.reduce((acc, s) => { (acc[s.category] ||= []).push(s); return acc; }, {});
  const cats = Object.keys(byCat).sort((a, b) => {
    const ia = secOrder.indexOf(a), ib = secOrder.indexOf(b);
    return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
  });

  const archiveService = async (s) => {
    try { await api.put(`/services/${s.id}`, { active: !s.active }); toast.success(s.active ? "Service archived" : "Service restored"); load(); }
    catch { toast.error("Could not update service"); }
  };
  const renameSection = async (cat) => {
    const nn = await promptDialog({ title: "Rename section", label: "Section name", defaultValue: cat, confirmText: "Rename" });
    if (!nn || nn.trim() === cat) return;
    try { await api.post("/service-sections/rename", { old: cat, new: nn.trim() }); toast.success("Section renamed"); load(); }
    catch { toast.error("Could not rename section"); }
  };
  const archiveSection = async (cat, archived) => {
    if (!(await confirmDialog({ title: archived ? "Archive section?" : "Restore section?", description: `${archived ? "Archive" : "Restore"} all services in “${cat}”?`, confirmText: archived ? "Archive" : "Restore" }))) return;
    try { await api.post("/service-sections/archive", { name: cat, archived }); toast.success(archived ? "Section archived" : "Section restored"); load(); }
    catch { toast.error("Could not update section"); }
  };
  const dropSection = async (target) => {
    if (!secDrag || secDrag === target) { setSecDrag(null); return; }
    const list = [...cats];
    const from = list.indexOf(secDrag), to = list.indexOf(target);
    list.splice(from, 1); list.splice(to, 0, secDrag);
    setSecOrder(list); setSecDrag(null);
    try { await api.put("/service-sections", { order: list }); toast.success("Section order saved"); }
    catch { toast.error("Could not save order"); load(); }
  };
  const dropService = async (cat, targetId) => {
    if (!svcDrag || svcDrag.cat !== cat || svcDrag.id === targetId) { setSvcDrag(null); return; }
    const items = [...byCat[cat]];
    const from = items.findIndex((x) => x.id === svcDrag.id);
    const to = items.findIndex((x) => x.id === targetId);
    const [m] = items.splice(from, 1); items.splice(to, 0, m);
    setSvcDrag(null);
    try { await api.post("/services/reorder", { ids: items.map((x) => x.id) }); toast.success("Service order saved"); load(); }
    catch { toast.error("Could not save order"); load(); }
  };
  // Touch-friendly move (drag-and-drop doesn't fire on tablets/phones).
  const moveSection = async (cat, dir) => {
    const list = [...cats]; const idx = list.indexOf(cat); const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    list.splice(idx, 1); list.splice(to, 0, cat);
    setSecOrder(list);
    try { await api.put("/service-sections", { order: list }); toast.success("Section order saved"); }
    catch { toast.error("Could not save order"); load(); }
  };
  const moveService = async (cat, id, dir) => {
    const items = [...byCat[cat]]; const idx = items.findIndex((x) => x.id === id); const to = idx + dir;
    if (to < 0 || to >= items.length) return;
    const [m] = items.splice(idx, 1); items.splice(to, 0, m);
    try { await api.post("/services/reorder", { ids: items.map((x) => x.id) }); toast.success("Service order saved"); load(); }
    catch { toast.error("Could not save order"); load(); }
  };

  const createService = async () => {
    if (!nw.client_name.trim() || !nw.category.trim()) { toast.error("Client-facing name and section are required"); return; }
    const lines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
    const catInput = nw.category.trim();
    const category = Object.keys(byCat).find((c) => c.toLowerCase() === catInput.toLowerCase()) || catInput;
    const payload = {
      client_name: nw.client_name.trim(),
      internal_name: nw.internal_name.trim() || undefined,
      category,
      short_description: nw.short_description,
      full_scope: nw.full_scope || nw.short_description,
      included: lines(nw.included),
      cass_responsibilities: lines(nw.cass_responsibilities),
      client_responsibilities: lines(nw.client_responsibilities),
      create_loe_schedule: nw.create_loe_schedule,
      pricing: { base_fee: Number(nw.base_fee) || 0, minimum_fee: Number(nw.minimum_fee) || 0, billing_frequency: nw.billing_frequency, recurring: nw.billing_frequency !== "one_off", vat_treatment: nw.vat_treatment, rounding: "none", rules: [] },
    };
    try { await api.post("/services", payload); toast.success("Service added to catalogue"); setShowNew(false); setNw(BLANK); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add service"); }
  };

  const save = async () => {
    await api.put(`/services/${edit.id}`, edit);
    toast.success("Service updated");
    setEdit(null);
    load();
  };

  return (
    <div>
      <PageHeader title="Service Catalogue" subtitle="Configurable services, scope and pricing (V2 components)." actions={<Button variant="outline" size="sm" onClick={() => navigate("/reports/adjustments")} data-testid="fee-adjustments-link">Fee adjustments</Button>} />
      <div className="p-8">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-cass-slate cursor-pointer">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-toggle" /> Show archived
          </label>
          {canEdit && (
            <div className="flex flex-wrap justify-end gap-2">
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => { importComponents(e.target.files?.[0]); e.target.value = ""; }} data-testid="import-components-input" />
              <Button variant="outline" onClick={downloadComponents} data-testid="download-components-btn"><Download className="w-4 h-4 mr-1" />Components spreadsheet</Button>
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-components-btn"><Upload className="w-4 h-4 mr-1" />Import components</Button>
              <Button className="bg-cass-navy text-white" onClick={() => setShowNew(true)} data-testid="new-service-btn"><Plus className="w-4 h-4 mr-1" />New service / section</Button>
            </div>
          )}
        </div>
        <Accordion type="multiple" className="space-y-3">
          {cats.map((cat) => {
            const items = showArchived ? byCat[cat] : byCat[cat].filter((s) => s.active !== false);
            if (!items.length) return null;
            const allArchived = byCat[cat].every((s) => !s.active);
            return (
            <AccordionItem key={cat} value={cat} draggable={canEdit} onDragStart={() => setSecDrag(cat)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropSection(cat)} className={`bg-white border border-border rounded-md px-4 ${secDrag === cat ? "ring-2 ring-cass-teal" : ""}`} data-testid={`section-${cat}`}>
              <div className="flex items-center">
                {canEdit && <GripVertical className="w-4 h-4 text-cass-slate mr-2 cursor-grab shrink-0" data-testid={`drag-section-${cat}`} />}
                <AccordionTrigger className="font-alt font-semibold text-cass-navy hover:no-underline flex-1">
                  {cat} <Badge variant="secondary" className="ml-2">{items.length}</Badge>{allArchived && <Badge variant="outline" className="ml-2 text-cass-slate">Archived</Badge>}
                </AccordionTrigger>
                {canEdit && (
                  <div className="flex items-center gap-1 ml-2">
                    <Button size="sm" variant="ghost" title="Move up" onClick={() => moveSection(cat, -1)} disabled={cats.indexOf(cat) === 0} data-testid={`section-up-${cat}`}><ChevronUp className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" title="Move down" onClick={() => moveSection(cat, 1)} disabled={cats.indexOf(cat) === cats.length - 1} data-testid={`section-down-${cat}`}><ChevronDown className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" title="Rename section" onClick={() => renameSection(cat)} data-testid={`rename-section-${cat}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" title={allArchived ? "Restore section" : "Archive section"} onClick={() => archiveSection(cat, !allArchived)} data-testid={`archive-section-${cat}`}>{allArchived ? <RotateCcw className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}</Button>
                  </div>
                )}
              </div>
              <AccordionContent>
                <div className="space-y-2">
                  {items.map((s) => (
                    <div key={s.id} data-testid={`service-${s.internal_name}`} draggable={canEdit} onDragStart={(e) => { e.stopPropagation(); setSvcDrag({ cat, id: s.id }); }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.stopPropagation(); dropService(cat, s.id); }} className={`flex items-center justify-between p-3 rounded-md border border-border ${!s.active ? "opacity-60" : ""} ${svcDrag?.id === s.id ? "ring-2 ring-cass-teal" : ""}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        {canEdit && <GripVertical className="w-4 h-4 text-cass-slate cursor-grab shrink-0" data-testid={`drag-service-${s.internal_name}`} />}
                      <div>
                        <div className="font-medium text-cass-navy">{s.client_name}{!s.active && <Badge variant="outline" className="ml-2 text-xs text-cass-slate">Archived</Badge>}{(() => { const d = ((s.pricing_v2 || {}).components || []).filter((c) => c.status === "draft").length; return d ? <Badge className="ml-2 text-[10px] bg-amber-500" data-testid={`drafts-badge-${s.internal_name}`}>{d} draft{d === 1 ? "" : "s"}</Badge> : null; })()}</div>
                        <div className="text-xs text-cass-slate">{s.short_description}</div>
                        <div className="text-xs text-cass-slate mt-1">
                          Base {gbp(s.pricing?.base_fee)} · Min {gbp(s.pricing?.minimum_fee)} · {s.pricing?.billing_frequency} · {(s.pricing?.rules || []).length} rules
                          {PER_YEAR[s.pricing?.billing_frequency] && <span className="text-cass-teal font-medium"> · ≈ {gbp((Number(s.pricing?.base_fee) || 0) * PER_YEAR[s.pricing?.billing_frequency])}/yr</span>}
                        </div>
                      </div>
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="ghost" title="Move up" onClick={() => moveService(cat, s.id, -1)} disabled={byCat[cat].findIndex((x) => x.id === s.id) === 0} data-testid={`service-up-${s.internal_name}`}><ChevronUp className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" title="Move down" onClick={() => moveService(cat, s.id, 1)} disabled={byCat[cat].findIndex((x) => x.id === s.id) === byCat[cat].length - 1} data-testid={`service-down-${s.internal_name}`}><ChevronDown className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => setEdit(s)} data-testid={`quickedit-${s.internal_name}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                          <Button size="sm" variant="outline" onClick={() => navigate(`/services/${s.id}/pricing`)} data-testid={`components-${s.internal_name}`}>Pricing</Button>
                          <Button size="sm" variant="outline" onClick={() => navigate(`/services/${s.id}/onboarding`)} data-testid={`onboarding-${s.internal_name}`}>Onboarding</Button>
                          <Button size="sm" variant="ghost" title={s.active ? "Archive" : "Restore"} onClick={() => archiveService(s)} data-testid={`archive-${s.internal_name}`}>{s.active ? <Archive className="w-3.5 h-3.5" /> : <RotateCcw className="w-3.5 h-3.5" />}</Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
            );
          })}
        </Accordion>
        {canEdit && <p className="text-xs text-cass-slate mt-3">Drag the <GripVertical className="w-3 h-3 inline" /> handle on a computer, or use the <ChevronUp className="w-3 h-3 inline" /><ChevronDown className="w-3 h-3 inline" /> buttons on a tablet/phone, to reorder sections and services.</p>}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="service-edit-dialog">
          <DialogHeader><DialogTitle>Edit {edit?.client_name}</DialogTitle><DialogDescription>Rename the service, move it to another section, and adjust its headline pricing.</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Client-facing name</Label><Input value={edit.client_name} onChange={(e) => setEdit({ ...edit, client_name: e.target.value })} data-testid="edit-client-name" /></div>
              <div><Label>Service key (for LoE schedule mapping)</Label>
                <div className="flex gap-2">
                  <Input value={edit.internal_name || ""} readOnly className="bg-cass-bone/40 font-mono text-xs" data-testid="edit-internal-name" />
                  <Button type="button" variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(edit.internal_name || ""); toast.success("Service key copied"); }} data-testid="copy-service-key">Copy</Button>
                </div>
                <p className="text-[11px] text-cass-slate mt-1">Add this exact key to a Service Schedule's “Service keys” field to attach it to this service.</p>
              </div>
              <div><Label>Section</Label><Input list="edit-categories" value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })} data-testid="edit-category" /><datalist id="edit-categories">{Object.keys(byCat).map((c) => <option key={c} value={c} />)}</datalist></div>
              <div><Label>Short description</Label><Textarea value={edit.short_description || ""} onChange={(e) => setEdit({ ...edit, short_description: e.target.value })} /></div>
              <div className="border-t border-border pt-3 space-y-2">
                <div><Label>Xero invoice description</Label><Input value={edit.xero_description || ""} onChange={(e) => setEdit({ ...edit, xero_description: e.target.value })} placeholder="Client-facing line text for Xero (defaults to the service name)" data-testid="edit-xero-description" /></div>
                <div><Label>Xero sales account code</Label><Input value={edit.xero_account_code || ""} onChange={(e) => setEdit({ ...edit, xero_account_code: e.target.value })} placeholder="Leave blank to use the CASS default" data-testid="edit-xero-account" /></div>
                <label className="flex items-center gap-2 text-sm text-cass-navy cursor-pointer">
                  <input type="checkbox" checked={!!edit.xero_repeating_separate} onChange={(e) => setEdit({ ...edit, xero_repeating_separate: e.target.checked })} data-testid="edit-xero-separate" />
                  Show as its own line on the monthly repeating invoice (not consolidated)
                </label>
                <p className="text-[11px] text-cass-slate">These Xero defaults flow through to the proposal and the Integrations screen. Monthly services sharing an account code are consolidated into one line; tick the box to force an individual line.</p>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-md border border-border bg-cass-bone/40">
                <Switch checked={!!edit.subject_to_fee_alignment} onCheckedChange={(v) => setEdit({ ...edit, subject_to_fee_alignment: v })} data-testid="edit-subject-to-fee-alignment" />
                <div><Label>Subject to Fee Alignment</Label><p className="text-xs text-cass-slate">When this service is selected on a proposal it's automatically added to Fee Alignment (one-off), calculated from the client's year end and the proposal start date.</p></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Base fee (£)</Label><Input data-testid="edit-base-fee" type="number" value={edit.pricing.base_fee} onChange={(e) => setEdit({ ...edit, pricing: { ...edit.pricing, base_fee: Number(e.target.value) } })} /></div>
                <div><Label>Minimum fee (£)</Label><Input data-testid="edit-min-fee" type="number" value={edit.pricing.minimum_fee} onChange={(e) => setEdit({ ...edit, pricing: { ...edit.pricing, minimum_fee: Number(e.target.value) } })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3 items-end rounded-md border border-cass-teal/30 bg-cass-teal/5 p-3" data-testid="edit-min-monthly-fee-block">
                <div><Label>Minimum Monthly Fee (£)</Label><Input data-testid="edit-min-monthly-fee" type="number" value={edit.min_monthly_fee ?? 0} onChange={(e) => setEdit({ ...edit, min_monthly_fee: Number(e.target.value) })} /><p className="text-[11px] text-cass-slate mt-1">Price floor for this service. The fee is calculated from components but never falls below this.</p></div>
                <div className="flex items-center gap-2 pb-2"><Switch checked={!!edit.min_fee_active} onCheckedChange={(v) => setEdit({ ...edit, min_fee_active: v })} data-testid="edit-min-fee-active" /><Label>Minimum Fee Active</Label></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Billing frequency</Label>
                  <Select value={edit.pricing.billing_frequency} onValueChange={changeEditFreq}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>VAT treatment</Label>
                  <Select value={edit.pricing.vat_treatment} onValueChange={(v) => setEdit({ ...edit, pricing: { ...edit.pricing, vat_treatment: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="standard">Standard 20%</SelectItem><SelectItem value="exempt">Exempt</SelectItem></SelectContent>
                  </Select></div>
              </div>
              <div className="text-xs text-cass-slate bg-cass-bone p-3 rounded-md">
                {(edit.pricing.rules || []).length} pricing rule(s). Full rule editor available via API; base/min/frequency editable here.
              </div>
              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex items-center gap-3">
                  <Switch checked={!!edit.clearance_required} onCheckedChange={(v) => setEdit({ ...edit, clearance_required: v })} data-testid="edit-clearance-required" />
                  <Label>Does this service require information from the previous accountant?</Label>
                </div>
                {edit.clearance_required && (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5" data-testid="edit-req-blocks">
                      {(edit.requirement_block_ids || []).map((id) => {
                        const b = rbList.find((x) => x.id === id);
                        return (
                          <Badge key={id} variant="outline" className="gap-1 bg-cass-bone text-cass-navy border-cass-navy/15" data-testid={`req-block-${id}`}>
                            {b ? b.name : id}
                            <button type="button" className="ml-1 text-cass-slate hover:text-red-500" onClick={() => setEdit({ ...edit, requirement_block_ids: (edit.requirement_block_ids || []).filter((x) => x !== id) })} data-testid={`req-block-remove-${id}`}>×</button>
                          </Badge>
                        );
                      })}
                      {!(edit.requirement_block_ids || []).length && <span className="text-xs text-cass-slate">No blocks selected yet.</span>}
                    </div>
                    <Select value="" onValueChange={(v) => { if (v && !(edit.requirement_block_ids || []).includes(v)) setEdit({ ...edit, requirement_block_ids: [...(edit.requirement_block_ids || []), v] }); }}>
                      <SelectTrigger data-testid="add-req-block"><SelectValue placeholder="Add a requirement block…" /></SelectTrigger>
                      <SelectContent>
                        {rbList.filter((b) => b.active && !(edit.requirement_block_ids || []).includes(b.id)).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {(edit.requirement_block_ids || []).length > 0 && (
                      <div className="text-xs bg-cass-bone/40 rounded-md p-3 space-y-2" data-testid="req-preview">
                        {(edit.requirement_block_ids || []).map((id) => {
                          const b = rbList.find((x) => x.id === id);
                          if (!b) return null;
                          return <div key={id}><div className="font-semibold text-cass-navy">{b.heading || b.name}</div><div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: b.content || "<em>(empty — add wording in the library)</em>" }} /></div>;
                        })}
                      </div>
                    )}
                    <p className="text-[11px] text-cass-slate">Wording is managed centrally in <button type="button" className="underline" onClick={() => navigate("/admin/requirement-blocks")}>Requirement Blocks</button>. One block can serve many services.</p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="bg-cass-teal text-white" data-testid="save-service-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNew} onOpenChange={(o) => !o && setShowNew(false)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="new-service-dialog">
          <DialogHeader><DialogTitle>New service</DialogTitle><DialogDescription>Add a service to the catalogue and (optionally) a matching Letter of Engagement schedule.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Client-facing name *</Label><Input value={nw.client_name} onChange={(e) => setNw({ ...nw, client_name: e.target.value })} placeholder="e.g. Management Reporting" data-testid="ns-client-name" /></div>
              <div>
                <Label>Section *</Label>
                <Input list="ns-categories" value={nw.category} onChange={(e) => setNw({ ...nw, category: e.target.value })} placeholder="Pick or type a new section" data-testid="ns-category" />
                <datalist id="ns-categories">{Object.keys(byCat).map((c) => <option key={c} value={c} />)}</datalist>
                <p className="text-[11px] text-cass-slate mt-1">Type a new name to create a new section.</p>
              </div>
            </div>
            <div><Label>Short description</Label><Textarea rows={2} value={nw.short_description} onChange={(e) => setNw({ ...nw, short_description: e.target.value })} data-testid="ns-desc" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Base fee (£)</Label><Input type="number" value={nw.base_fee} onChange={(e) => setNw({ ...nw, base_fee: e.target.value })} data-testid="ns-base-fee" /></div>
              <div><Label>Minimum fee (£)</Label><Input type="number" value={nw.minimum_fee} onChange={(e) => setNw({ ...nw, minimum_fee: e.target.value })} data-testid="ns-min-fee" /></div>
              <div><Label>Billing frequency</Label>
                <Select value={nw.billing_frequency} onValueChange={(v) => setNw({ ...nw, billing_frequency: v })}>
                  <SelectTrigger data-testid="ns-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="border-t border-border pt-3">
              <div className="text-sm font-medium text-cass-navy mb-2">Letter of Engagement scope</div>
              <div><Label>Scope intro</Label><Textarea rows={2} value={nw.full_scope} onChange={(e) => setNw({ ...nw, full_scope: e.target.value })} placeholder="Optional overview of this service for the LoE" data-testid="ns-scope-intro" /></div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div><Label>What's included</Label><Textarea rows={4} value={nw.included} onChange={(e) => setNw({ ...nw, included: e.target.value })} placeholder="One item per line" data-testid="ns-included" /></div>
                <div><Label>Our responsibilities</Label><Textarea rows={4} value={nw.cass_responsibilities} onChange={(e) => setNw({ ...nw, cass_responsibilities: e.target.value })} placeholder="One item per line" data-testid="ns-cass-resp" /></div>
                <div><Label>Your responsibilities</Label><Textarea rows={4} value={nw.client_responsibilities} onChange={(e) => setNw({ ...nw, client_responsibilities: e.target.value })} placeholder="One item per line" data-testid="ns-client-resp" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-cass-slate mt-3 cursor-pointer">
                <input type="checkbox" checked={nw.create_loe_schedule} onChange={(e) => setNw({ ...nw, create_loe_schedule: e.target.checked })} data-testid="ns-create-schedule" />
                Add a matching schedule to the Letter of Engagement (shows when this service is selected)
              </label>
            </div>
          </div>
          <DialogFooter><Button onClick={createService} className="bg-cass-navy text-white" data-testid="save-new-service-btn">Add service</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o && !importing) { setPreview(null); setPreviewFile(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-preview-dialog">
          <DialogHeader>
            <DialogTitle>Review import changes</DialogTitle>
            <DialogDescription>Nothing is saved until you click "Apply import". Each listed service will have its pricing components fully replaced by the spreadsheet.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-sm">
                <Badge variant="secondary" data-testid="preview-matched">{preview.services_matched} service{preview.services_matched === 1 ? "" : "s"} to update</Badge>
                <Badge variant="outline" className="text-cass-teal border-cass-teal/40" data-testid="preview-added">+{preview.totals.added} new</Badge>
                <Badge variant="outline" className="text-cass-bronze border-cass-bronze/40" data-testid="preview-changed">~{preview.totals.changed} changed</Badge>
                <Badge variant="outline" className="text-red-600 border-red-300" data-testid="preview-removed">−{preview.totals.removed} removed</Badge>
                {preview.skipped_unmatched?.length > 0 && <Badge variant="destructive" data-testid="preview-skipped">{preview.skipped_unmatched.length} skipped</Badge>}
              </div>
              <div className="border border-border rounded-md divide-y divide-border max-h-[45vh] overflow-y-auto">
                {preview.summary.filter((r) => !r.matched || r.added.length || r.removed.length || r.changed.length || r.before !== r.after).map((r, i) => (
                  <div key={i} className="px-4 py-2.5 text-sm" data-testid={`preview-row-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-cass-navy">{r.service}</span>
                      {r.matched
                        ? <span className="text-xs text-cass-slate tabular whitespace-nowrap">{r.before} → {r.after} components</span>
                        : <Badge variant="destructive" className="text-[10px]">no matching service — skipped</Badge>}
                    </div>
                    {r.matched && (r.added.length > 0 || r.removed.length > 0 || r.changed.length > 0) && (
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                        {r.added.length > 0 && <span className="text-cass-teal">+{r.added.length} new: {r.added.slice(0, 4).join(", ")}{r.added.length > 4 ? "…" : ""}</span>}
                        {r.changed.length > 0 && <span className="text-cass-bronze">~{r.changed.length} changed: {r.changed.slice(0, 4).join(", ")}{r.changed.length > 4 ? "…" : ""}</span>}
                        {r.removed.length > 0 && <span className="text-red-600">−{r.removed.length} removed: {r.removed.slice(0, 4).join(", ")}{r.removed.length > 4 ? "…" : ""}</span>}
                      </div>
                    )}
                  </div>
                ))}
                {preview.summary.every((r) => r.matched && !r.added.length && !r.removed.length && !r.changed.length && r.before === r.after) && (
                  <div className="px-4 py-6 text-center text-sm text-cass-slate" data-testid="preview-nochange">No changes detected — the spreadsheet matches the current components.</div>
                )}
              </div>
              {preview.warnings?.length > 0 && (
                <div className="text-xs text-cass-bronze" data-testid="preview-warnings">⚠ {preview.warnings.slice(0, 5).join(" · ")}</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPreview(null); setPreviewFile(null); }} disabled={importing} data-testid="preview-cancel-btn">Cancel</Button>
            <Button className="bg-cass-navy text-white" onClick={applyImport} disabled={importing || !preview?.services_matched} data-testid="preview-apply-btn">{importing ? "Applying…" : "Apply import"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
